import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";
import MissionCard from "./MissionCard";
import SpeakerButton from "./SpeakerButton";
import { createMission } from "../lib/api";

const ICON_OPTIONS = [
  { value: "wrench", label: "Build" },
  { value: "shield", label: "Secure" },
  { value: "rocket", label: "Launch" },
  { value: "brain", label: "Learn" },
  { value: "telescope", label: "Explore" },
  { value: "chart", label: "Track" },
  { value: "code", label: "Code" },
  { value: "star", label: "Special" },
];

const COLOR_OPTIONS = ["gold", "red", "white"];

export const MissionBoard = ({ missions, onComplete, onDelete, onMissionAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", summary: "", icon: "wrench", color: "gold", coins: 15, xp: 30 });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const created = await createMission({ ...form, coins: Number(form.coins), xp: Number(form.xp) });
    onMissionAdded && onMissionAdded(created);
    setForm({ title: "", summary: "", icon: "wrench", color: "gold", coins: 15, xp: 30 });
    setOpen(false);
  };

  const active = missions.filter(m => !m.completed);
  const done = missions.filter(m => m.completed);

  return (
    <div data-testid="mission-board">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-3xl md:text-4xl font-black">Mission Board</h2>
          <SpeakerButton
            text="This is your Mission Board. Each mission earns Academy Coins and experience. Complete them to climb the ranks."
            testid="speaker-mission-board"
            size="sm"
          />
        </div>
        <button
          onClick={() => setOpen(true)}
          className="btn-eigoo btn-gold"
          data-testid="open-create-mission"
        >
          <Plus size={20} /> New Mission
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.form
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={submit}
            className="eigoo-glass rounded-3xl p-5 md:p-6 mb-6"
            data-testid="create-mission-form"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-xl">Create Mission</div>
              <button type="button" onClick={() => setOpen(false)} className="hit-target text-white/50 hover:text-white" data-testid="close-create-mission">
                <X />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Mission title"
                required
                data-testid="mission-title-input"
                className="md:col-span-2 px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 focus:border-yellow-300/60 outline-none text-lg"
              />
              <input
                value={form.summary}
                onChange={e => setForm({ ...form, summary: e.target.value })}
                placeholder="Short summary"
                data-testid="mission-summary-input"
                className="md:col-span-2 px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 focus:border-yellow-300/60 outline-none"
              />
              <select
                value={form.icon}
                onChange={e => setForm({ ...form, icon: e.target.value })}
                data-testid="mission-icon-select"
                className="px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 outline-none"
              >
                {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                value={form.color}
                onChange={e => setForm({ ...form, color: e.target.value })}
                data-testid="mission-color-select"
                className="px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 outline-none"
              >
                {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                type="number"
                min="1" max="200"
                value={form.coins}
                onChange={e => setForm({ ...form, coins: e.target.value })}
                placeholder="Coins"
                data-testid="mission-coins-input"
                className="px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 outline-none"
              />
              <input
                type="number"
                min="1" max="500"
                value={form.xp}
                onChange={e => setForm({ ...form, xp: e.target.value })}
                placeholder="XP"
                data-testid="mission-xp-input"
                className="px-4 py-3 rounded-2xl bg-black/40 border border-yellow-300/20 outline-none"
              />
            </div>
            <button type="submit" className="btn-eigoo btn-gold mt-4" data-testid="submit-mission">
              <Plus size={18} /> Add Mission
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      {active.length === 0 && done.length === 0 ? (
        <div className="eigoo-glass rounded-3xl p-10 text-center" data-testid="missions-empty">
          <p className="font-display text-xl text-white/60">No missions yet. Use the microphone above or tap "New Mission".</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6" data-testid="missions-active-grid">
            <AnimatePresence>
              {active.map(m => (
                <MissionCard key={m.id} mission={m} onComplete={onComplete} onDelete={onDelete} />
              ))}
            </AnimatePresence>
          </div>
          {done.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <div className="text-xs uppercase tracking-[0.25em] text-white/40 font-display">Logbook · Completed</div>
                <div className="flex-1 divider-gold" />
              </div>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {done.map(m => (
                  <MissionCard key={m.id} mission={m} onComplete={onComplete} onDelete={onDelete} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MissionBoard;
